<template>
  <div class="flex items-center justify-center w-full">
    <!-- Dropzone -->
    <div v-if="pages.length === 0" @click="fileInput.click()" @dragover.prevent="isDragging = true"   @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" class="w-full max-w-4xl mb-10 bg-gradient-to-b from-green-50 to-white hover:from-white hover:to-green-50 mt-5 transition-all duration-300 cursor-pointer shadow-lg rounded-2xl flex flex-col items-center justify-center text-center py-8 px-4 md:py-16 md:px-8 relative overflow-hidden border-2" :class="[ isDragging  ? 'border-[#24bd64ea] bg-green-100 shadow-2xl' : 'border-[#2ecc71]']">
      <!-- Loading indicator -->
      <div v-if="isProcessing" class="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 rounded-2xl">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-[#e5e7eb] border-t-[#2ecc71] mb-4"></div>
          <p class="text-black font-semibold text-lg">{{ (translations.processing_pages || 'Processing :count pages...').replace(':count', totalPagesBeingProcessed) }}</p>
          <p class="text-gray-600 text-sm mt-2">{{ translations.take_moment || 'This may take a moment' }}</p>
      </div>
      <div class="relative z-10 flex flex-col items-center">
          <div class="mx-auto w-20 h-20 flex items-center justify-center mb-4">
            <img :src="PDF_ICON" alt="PDF Upload" class="w-full h-full object-contain" />
          </div>
            <!-- Upload Button -->
          <button @click.stop="fileInput.click()" class="bg-[#2ecc71] text-white font-semibold px-8 py-3 rounded-lg active:bg-[#24bd64ea] 
          transition-all duration-200 flex items-center gap-2 shadow-md">
          <Upload :size="20" />
          <span class="font-semibold">{{ translations.select_files || 'Select Files' }}</span>
          </button>
          <p class="mb-5 font-semibold text-base text-black mt-6">
          {{ translations.drag_drop_pdf || 'Drag and Drop PDFs here' }}
          </p>
          <p class="text-sm text-gray-700 font-medium">
          {{ translations.paste_hint || 'or press CTRL + V to paste' }}
          </p>
      </div>
    </div>
      <!-- Main Editor -->
      <div v-else class="flex flex-col lg:flex-row w-full gap-4 lg:gap-8 px-2 md:px-0 pb-20">
          <aside class="w-full lg:w-[30%] bg-gray-50 mt-1 p-3 md:p-4 shadow-md border-t-4 border-green-400 rounded-md lg:sticky lg:top-24 h-fit z-20">
              <!-- Signature block -->
              <div class="mb-4">
                <div>
                  <!-- Signature Box -->
                  <div
                    class="bg-white border border-gray-200 rounded-xl p-3 md:p-4 
                          hover:border-green-400 hover:shadow-md transition-all duration-300
                          cursor-pointer relative"
                    :class="{ 'cursor-grab': signatureImage, 'cursor-not-allowed': !signatureImage }"
                    @click="openSignModal"
                    draggable="true"
                    @dragstart="handleDragStart"
                    @dragend="handleDragEnd"
                  >
                    <!-- Title -->
                    <p class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      {{ translations.your_signature || '✍️ Your Signature' }}
                    </p>

                    <!-- Signature Image -->
                    <div
                      v-if="signatureImage"
                      class="flex items-center justify-center py-3"
                    >
                      <img
                        :src="signatureImage"
                        class="w-[160px] md:w-[200px] h-[60px] md:h-[70px] object-contain pointer-events-none drop-shadow-sm"
                        alt="Signature"
                      />
                    </div>

                    <!-- Placeholder State -->
                    <div
                      v-else
                      class="h-14 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 border border-dashed border-gray-300"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/download-aAo7Y7ChvS2Yb4TH8tROwPYzT8qh1O.png"
                        class="h-10 opacity-70"
                      />
                    </div>

                    <!-- Drag hint -->
                    <p
                      v-if="signatureImage"
                      class="text-xs text-green-600 mt-3 text-center font-medium animate-pulse"
                    >
                      {{ translations.drag_hint || 'Drag & drop to PDF' }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="max-w-full">
                <div class="grid grid-cols-2 gap-2 md:gap-3">
                    <!-- Feature Card -->
                    <div
                      v-for="(feature, index) in features"
                      :key="index"
                      class="group relative overflow-hidden rounded-xl bg-white border border-gray-200 transition-all duration-300 hover:shadow-lg hover:border-green-300 opacity-60"
                    >
                      <!-- Gradient Overlay on Hover -->
                      <div class="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <!-- Content -->
                      <div class="relative p-2 md:p-3 flex flex-col h-full">
                        <!-- Icon Container -->
                        <div class="w-5 h-5 rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 flex items-center justify-center mb-2 md:mb-4 group-hover:scale-105 transition-transform duration-300">
                          <component
                            :is="feature.icon"
                            class="w-4 h-4 md:w-5 md:h-5 text-green-600"
                          />
                        </div>

                        <!-- Title -->
                        <h3 class="text-[10px] md:text-xs font-semibold text-gray-900 mb-1 md:mb-2 truncate">
                          {{ feature.title }}
                        </h3>

                        <!-- Progress Bar & Quarter -->
                        <div class="flex items-center gap-2 md:gap-3 pt-2 md:pt-4 border-t border-gray-100">
                          <div class="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              class="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                              :style="{ width: feature.progress + '%' }"
                            />
                          </div>
                        </div>

                        <!-- Coming Soon Badge -->
                        <div class="absolute top-0.5 right-1 md:right-3">
                          <span class="inline-flex items-center gap-1.5 px-1 py-1 rounded-md bg-green-100 text-green-700 text-[6px] md:text-[8px] font-semibold">
                            <span class="w-1 h-1 ml-0.5 rounded-full bg-green-500 animate-pulse" />
                            {{ translations.soon || 'Soon' }}
                          </span>
                        </div>
                      </div>
                    </div>
                </div>
              </div>
          </aside>
          <div class="w-full lg:w-[70%] flex items-start justify-center">
              <div class="w-full max-w-7xl">
                  <div class="mx-auto">
                      <!-- Toolbar -->
                      <div class="bg-white rounded-md shadow-md p-3 md:p-4 border-b border-green-100 border-t-4 border-green-400 top-16 md:top-20 z-30 transition-all">
                        <div class="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <p class="text-gray-800 text-lg md:text-xl w-full sm:w-auto text-center sm:text-left">
                                <span v-if="fileInfo.uniqueFiles.length">
                                  {{ translations.editing || 'Editing :' }}
                                <span class="font-semibold block sm:inline truncate max-w-[200px] sm:max-w-[300px] mx-auto sm:mx-0">
                                    {{
                                      fileInfo.fileTextDisplay.length > 30
                                        ? fileInfo.fileTextDisplay.slice(0, 30) + '...'
                                        : fileInfo.fileTextDisplay
                                    }}
                                  </span>
                                </span>
                            </p>
                            
                            <div class="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                              <button @click="reloadPage" class="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 md:px-4 rounded-lg flex gap-2 font-semibold transition shadow-sm text-sm md:text-base flex-1 sm:flex-initial justify-center">
                              <RotateCcw :size="18" class="mt-0.5" />
                              {{ translations.restart || 'Restart' }}
                              </button>
                               <button
                              @click="saveAndDownload"
                              :disabled="isDownloading"
                              class="bg-[#2ecc71] hover:bg-[#24bd64ea] disabled:opacity-70 disabled:cursor-not-allowed 
                                      text-white px-3 py-2 md:px-4 rounded-md flex gap-2 font-semibold transition text-sm md:text-base shadow-sm hover:shadow-md flex-1 sm:flex-initial justify-center">
                              <!-- Loader -->
                              <span v-if="isDownloading" class="flex items-center gap-2">
                                  <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                  <path class="opacity-75" fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z">
                                  </path>
                                  </svg>
                                  {{ translations.processing || 'Processing...' }}
                              </span>

                              <!-- Normal Button -->
                              <span v-else class="flex items-center gap-2">
                                  <Download :size="20" class="mt-0.5" />
                                  {{ translations.download_pdf || 'Download PDF' }}
                              </span>
                              </button>
                            </div>
                        </div>
                      </div>
                      <!-- Processing pages message -->
                      <div v-if="isProcessing" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center gap-3">
                        <div class="animate-spin rounded-full h-5 w-5 border-2 border-green-400 border-t-green-600"></div>
                        <p class="text-green-700 font-medium">{{ (translations.processing_pages || 'Processing :count pages...').replace(':count', totalPagesBeingProcessed) }} {{ translations.appear_shortly || 'it will appear shortly' }}</p>
                      </div>
                      <div class="mt-3" 
                        :style="pages.length > 15 ? `max-height: ${maxHeight}px` : ''">
                        <!-- Pages Grid -->
                        <div class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 mb-8">
                          <div 
                            v-for="element in pages" 
                            :key="element.uid"
                            class="transition-all relative z-0"
                            :class="[element.isLoading ? 'opacity-75' : '']"
                          >
                            <!-- Loading overlay -->
                            <div v-if="element.isLoading" class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20 rounded-lg">
                              <div class="flex flex-col items-center gap-2">
                                <div class="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-green-400"></div>
                                <p class="text-white text-xs font-semibold">{{ translations.loading || 'Loading...' }}</p>
                              </div>
                            </div>
                            <!-- PDF Page with Drop Zone -->
                            <div 
                              class="flex items-center justify-center p-0.5 relative z-0 w-full max-w-4xl mx-auto"
                              @drop="handleSignatureDrop($event, element.uid)"
                              @dragover.prevent
                              @dragenter.prevent="element.isDragOver = true"
                              @dragleave.prevent="element.isDragOver = false"
                              :class="{ 'ring-4 ring-green-400': element.isDragOver }"
                            >
                              <img :src="element.thumbnail" class="max-w-full max-h-full object-contain shadow-2xl pointer-events-none" />
                              
                              <!-- Placed Signatures on this page -->
                              <div 
                                v-for="sig in getSignaturesForPage(element.uid)" 
                                :key="sig.id"
                                class="absolute cursor-move group hover:outline hover:outline-2 hover:outline-dashed hover:outline-blue-400 hover:outline-offset-2"
                                :style="{
                                  left: sig.x + '%',
                                  top: sig.y + '%',
                                  width: sig.width + '%',
                                  height: sig.height + '%'
                                }"
                                @mousedown.prevent="startDragSignature($event, sig)"
                              >
                                <img :src="sig.image" class="w-full h-full object-contain pointer-events-none" />
                                                              <!-- Edit button - Top Right (first) -->
                                <button
                                  @click.stop="openSignModal"
                                  class="absolute -top-4 right-6 bg-gray-200 hover:bg-gray-300 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
                                  :title="translations.edit_signature || 'Edit Signature'"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                                    <path d="m15 5 4 4"/>
                                  </svg>
                                </button>
                                
                                <!-- Delete button - Top Right (second) -->
                                <button
                                  @click.stop="deleteSignature(sig.id)"
                                  class="absolute -top-4 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                                  :title="translations.delete || 'Delete'">
                                  <X :size="14" />
                                </button>
                                
                                <!-- Resize handle - Bottom Right -->
                                <div
                                  @mousedown.stop.prevent="startResize($event, sig)"
                                  class="absolute -bottom-3 -right-3 w-5 h-5 bg-blue-500 rounded cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                                  :title="translations.resize || 'Resize'"
                                ></div>
v>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    <!-- Signature Modal -->
    <div v-if="showSignModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] pointer-events-auto p-3">
      <!-- Close button - Fixed to screen top-right -->
      <button 
        @click="showSignModal = false" 
        class="fixed top-6 right-6 bg-white hover:bg-gray-100 text-gray-700 hover:text-black rounded-full p-2 shadow-lg transition-all
         duration-200 z-[10001] border-2 border-gray-200 hover:border-gray-300"
        :title="translations.close || 'Close'">
        <X :size="24" />
      </button>
      
      <!-- Modal Content -->
      <div class="bg-white w-auto max-w-6xl rounded-xl shadow-2xl relative z-[10000]">
        <SignPdf :isEmbedded="true" :existingSignature="signatureData" @signed="handleSigned" />
      </div>
    </div>
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import SignPdf from './SignPdf.vue'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import { Upload, RotateCcw, RotateCw, Trash2, Download, X, Text, Image, Video, Table, DollarSign, ListOrdered, Scissors, Type } from 'lucide-vue-next'

const PDF_ICON = 'data:image/png;base64,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'

const isDownloading = ref(false)

// Props
const props = defineProps({
  initialSignature: {
    type: [String, Object],
    default: null
  },
  translations: {
    type: Object,
    default: () => ({})
  }
})

// Set worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const showSignModal = ref(false)
const signatureData = ref(props.initialSignature)

function openSignModal() {
  showSignModal.value = true
}

function handleSigned(signatureDataUrl) {
  // Store signature data
  signatureData.value = signatureDataUrl
  // Close modal
  showSignModal.value = false
  console.log('Signature saved:', signatureDataUrl)
}

const features = computed(() => [
  {
    title: props.translations.feature_text || 'Text Formatting',
    icon: Text,
  },
  {
    title: props.translations.feature_image || 'Image Gallery',
    icon: Image,
  },
  {
    title: props.translations.feature_video || 'Video Integration',
    icon: Video,
  },
  {
    title: props.translations.feature_table || 'Data Tables',
    icon: Table,
  },
  {
    title: props.translations.feature_page_breaks || 'Page Breaks',
    icon: Scissors,
  },
  {
    title: props.translations.feature_form_fields || 'Form Fields',
    icon: Type,
  }
])

// Computed property to get image from signature data
const signatureImage = computed(() => {
  if (!signatureData.value) return null
  // If it's an object, return the image property
  if (typeof signatureData.value === 'object') {
    return signatureData.value.image
  }
  // If it's a string, return it directly (old format)
  return signatureData.value
})

// Watch for signature changes and update all placed signatures
watch(signatureImage, (newImage) => {
  if (newImage && placedSignatures.value.length > 0) {
    // Update all placed signatures with new image
    placedSignatures.value.forEach(sig => {
      sig.image = newImage
    })
    console.log('Updated all placed signatures with new signature')
  }
})

// Emits
const emit = defineEmits(['back-to-sign', 'pdf-uploaded'])

// Refs
const pages = ref([])
const pdfjsDocs = ref({})
const pdfLibDocs = ref({})
const previewPages = ref([])
const isDragging = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
const fileInput = ref(null)
const previewCanvas = ref(null)

// Signature placement system
const placedSignatures = ref([])
let signatureIdCounter = 0
const isDraggingSignature = ref(false)
const isResizing = ref(false)
let currentDragSignature = null
let dragStartX = 0
let dragStartY = 0
let resizeStartWidth = 0
let resizeStartHeight = 0

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  const fileCount = uniqueFiles.length

  // Page plural
  const pageText = pages.value.length === 1 
    ? (props.translations.page || 'Page') 
    : (props.translations.pages || 'Pages')

  // File text logic:
  let fileTextDisplay = ''

  if (fileCount <= 2) {
    // 1 ya 2 files → names show
    fileTextDisplay = uniqueFiles.join(', ')
  } else {
    // 3+ files → sirf count show
    fileTextDisplay = (props.translations.files_count || ':count Files').replace(':count', fileCount)
  }

  return { uniqueFiles, fileCount, fileTextDisplay, pageText }
})

const handleDrop = (e) => {
  isDragging.value = false
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) {
    alert(props.translations.alert_pdf_only || 'Please select PDF files only')
    return
  }

  isProcessing.value = true

  for (const file of pdfFiles) {
    try {
      const buffer = await file.arrayBuffer()

      // Load with pdf-lib
      const pdfLibDoc = await PDFDocument.load(buffer)
      pdfLibDocs.value[file.name] = pdfLibDoc

      // Load with pdfjs
      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      pdfjsDocs.value[file.name] = pdfjsDoc

      totalPagesBeingProcessed.value = pdfjsDoc.numPages

      // Generate thumbnails with loading state
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        pages.value.push({
          uid: uidCounter++,
          fileName: file.name,
          pageNum: i,
          rotation: 0,
          thumbnail: '',
          isLoading: true
        })
      }

      // Render pages sequentially
      let pageIndex = pages.value.length - pdfjsDoc.numPages
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const { thumbnail } = await renderPage(pdfjsDoc, i, 0)
        if (pages.value[pageIndex]) {
          pages.value[pageIndex].thumbnail = thumbnail
          pages.value[pageIndex].isLoading = false
        }
        pageIndex++
      }
    } catch (error) {
      console.error('Error processing PDF:', error)
      alert(props.translations.alert_error_processing || 'Error processing PDF file. Please try again.')
    }
  }

  isProcessing.value = false
  totalPagesBeingProcessed.value = 0
  
  // Emit event that PDF has been uploaded
  if (pages.value.length > 0) {
    emit('pdf-uploaded')
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);

  const viewport = page.getViewport({
    scale: 2.5,
    rotation,
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  return { thumbnail: canvas.toDataURL("image/png") };
};


const toggleSelect = (uid) => {
  const idx = selectedUids.value.indexOf(uid)
  if (idx === -1) selectedUids.value.push(uid)
  else selectedUids.value.splice(idx, 1)
}

const renderPreviewPage = async (index) => {
  if (!previewPages.value[index]) return

  await nextTick()

  const canvas = previewCanvas.value
  if (!canvas) {
  console.error('Preview canvas not found')
    return
  }

  const page = previewPages.value[index]
  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) {
    console.error('PDF document not loaded for preview')
    return
  }

  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)

    // Get container size for responsive scaling
    const container = canvas.parentElement
    const maxWidth = container.clientWidth * 0.9
    const maxHeight = container.clientHeight * 0.9

    // Get original viewport
    let viewport = pdfPage.getViewport({ scale: 1, rotation: page.rotation })
    let scale = Math.min(maxWidth / viewport.width, maxHeight / viewport.height, 3) // Max scale 3
    scale = Math.max(scale, 0.5) // Min scale

    viewport = pdfPage.getViewport({ scale, rotation: page.rotation })

    // Set canvas size
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Use requestAnimationFrame to ensure rendering after resize
    requestAnimationFrame(async () => {
      try {
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        }
        await pdfPage.render(renderContext).promise
      } catch (err) {
        console.error('Render failed:', err)
      }
    })
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const createMergedPdf = async () => {
  // 1. Create a fresh PDF and copy all pages FIRST (without modification)
  const mergedPdf = await PDFDocument.create()
  const pageUidMap = [] // To map new page index -> pageUid

  for (const pageInfo of pages.value) {
    const sourcePdfDoc = pdfLibDocs.value[pageInfo.fileName]
    if (!sourcePdfDoc) continue

    // Use copyPages (preserves vectors/text) but don't modify yet
    const [copiedPage] = await mergedPdf.copyPages(sourcePdfDoc, [pageInfo.pageNum - 1])
    mergedPdf.addPage(copiedPage)
    pageUidMap.push(pageInfo.uid)
  }

  // 2. "Sanitize" the PDF by saving and reloading
  // This fixes the 'Expected instance of PDFArray2' error by flattening internal structures
  // before we attempt to modify the pages (add signatures).
  const intermediateBytes = await mergedPdf.save()
  const finalPdf = await PDFDocument.load(intermediateBytes)
  
  // 3. Draw Signatures on the clean PDF
  const finalPages = finalPdf.getPages()
  
  for (let i = 0; i < finalPages.length; i++) {
    const newPage = finalPages[i]
    const pageUid = pageUidMap[i]
    
    // Get signatures for this specific page
    const signaturesForPage = placedSignatures.value.filter(sig => sig.pageUid === pageUid)

    for (const sig of signaturesForPage) {
      try {
        let imageData = sig.image
        if (typeof imageData === 'string' && imageData.includes('base64,')) {
          imageData = imageData.split('base64,')[1]
        }

        const bytes = Uint8Array.from(atob(imageData), c => c.charCodeAt(0))
        let embeddedSig

        try {
          embeddedSig = await finalPdf.embedPng(bytes)
        } catch (pngError) {
          try {
            embeddedSig = await finalPdf.embedJpg(bytes)
          } catch (jpgError) {
            console.error('Signature embed failed:', jpgError)
            continue
          }
        }

        const { width, height } = newPage.getSize()
        
        // Calculate dimensions from percentage
        // If stored as %, we convert to points based on Page Width/Height
        const layoutW = (sig.width / 100) * width
        const layoutH = (sig.height / 100) * height
        
        // Convert Position
        const x = (sig.x / 100) * width
        
        // Start Y from top: (sig.y / 100) * height is distance from top
        // PDF Y 0 is bottom
        // so Bottom Y = height - distTop - objectHeight
        const y = height - (sig.y / 100) * height - layoutH

        newPage.drawImage(embeddedSig, {
          x,
          y,
          width: layoutW,
          height: layoutH,
        })
      } catch (err) {
        console.error('Error drawing signature:', err)
      }
    }
  }

  return await finalPdf.save()
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true   // Loader ON

    await nextTick()             // UI freeze na ho
    await new Promise(resolve => setTimeout(resolve, 50)) // Device hang se bachao

    const bytes = await createMergedPdf()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `organized-${Date.now()}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
  } finally {
    isDownloading.value = false  // Loader OFF
  }
}

const reloadPage = () => {
  window.location.reload()
}

// Calculate maxHeight dynamically based on 15 items
const maxHeight = computed(() => {
  // Estimate item height including padding/margin
  const itemHeight = 228 // 8rem = 128px
  const gap = 16 // Tailwind gap-4 = 1rem = 16px

  // Determine number of columns based on Tailwind breakpoints
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 5

  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

// Signature Drag-Drop Functions
function handleDragStart(e) {
  if (!signatureImage.value) {
    e.preventDefault()
    return
  }
  e.dataTransfer.effectAllowed = 'copy'
  e.dataTransfer.setData('text/plain', 'signature')
}

function handleDragEnd() {
  // Cleanup after drag
  isDropping = false
}

let isDropping = false

function handleSignatureDrop(e, pageUid) {
  e.preventDefault()
  e.stopPropagation()
  
  if (!signatureImage.value || isDropping) return
  
  isDropping = true
  
  // Use currentTarget to ensure we get the container bounds (which should match the image due to w-fit)
  const rect = e.currentTarget.getBoundingClientRect()
  
  // Calculate position as percentage
  // We clamp to ensure it stays roughly inside, but allow some bleeding edge if needed
  const xPx = e.clientX - rect.left
  const yPx = e.clientY - rect.top
  
  const x = (xPx / rect.width) * 100
  const y = (yPx / rect.height) * 100
  
  // Calculate default size as percentage relative to the page container
  // Default: 150px wide, 60px high (approx)
  // We convert this to % so it scales with the PDF page
  const widthPct = (150 / rect.width) * 100
  const heightPct = (60 / rect.height) * 100
  
  // Create new signature placement
  const newSignature = {
    id: signatureIdCounter++,
    pageUid: pageUid,
    image: signatureImage.value,
    x: Math.max(0, Math.min(x, 100)), // Allow full range 0-100
    y: Math.max(0, Math.min(y, 100)),
    width: widthPct,  // Store as %
    height: heightPct // Store as %
  }
  
  placedSignatures.value.push(newSignature)
  
  // Reset drag over state
  const page = pages.value.find(p => p.uid === pageUid)
  if (page) page.isDragOver = false
  
  // Reset flag after a short delay
  setTimeout(() => {
    isDropping = false
  }, 100)
}

function getSignaturesForPage(pageUid) {
  return placedSignatures.value.filter(sig => sig.pageUid === pageUid)
}

function deleteSignature(signatureId) {
  const index = placedSignatures.value.findIndex(sig => sig.id === signatureId)
  if (index !== -1) {
    placedSignatures.value.splice(index, 1)
  }
}

// Move signature
function startDragSignature(e, signature) {
  e.stopPropagation()
  currentDragSignature = signature
  isDraggingSignature.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  
  // We need to know container dimensions for precise pixel->% drag if we wanted pixel-perfect drag
  // But current implementation uses a fixed sensitivity or direct mapping
  // Let's improve onDragSignature to be container-aware if possible, 
  // but for now the existing logic (delta * sensitivity) is a bit loose.
  // Ideally: deltaPct = (deltaPx / rect.width) * 100
  
  // Allow grabbing container from the DOM for context
  const el = e.target.closest('.relative') // The signature element's parent container
  if (el) {
    const rect = el.getBoundingClientRect()
    currentDragContainerWidth = rect.width
    currentDragContainerHeight = rect.height
  }
  
  document.addEventListener('mousemove', onDragSignature)
  document.addEventListener('mouseup', stopDragSignature)
}

let currentDragContainerWidth = 1000 // fallback
let currentDragContainerHeight = 1000 // fallback

function onDragSignature(e) {
  if (!currentDragSignature || !isDraggingSignature.value) return
  
  const deltaX = e.clientX - dragStartX
  const deltaY = e.clientY - dragStartY
  
  // Convert pixels to % based on container size
  const deltaXPct = (deltaX / currentDragContainerWidth) * 100
  const deltaYPct = (deltaY / currentDragContainerHeight) * 100
  
  currentDragSignature.x += deltaXPct
  currentDragSignature.y += deltaYPct
  
  // Keep within bounds (0-100 mostly)
  currentDragSignature.x = Math.max(0, Math.min(currentDragSignature.x, 99))
  currentDragSignature.y = Math.max(0, Math.min(currentDragSignature.y, 99))
  
  dragStartX = e.clientX
  dragStartY = e.clientY
}

function stopDragSignature() {
  isDraggingSignature.value = false
  currentDragSignature = null
  document.removeEventListener('mousemove', onDragSignature)
  document.removeEventListener('mouseup', stopDragSignature)
}

// Resize signature
let resizeStartWidthPct = 0
let resizeStartHeightPct = 0

function startResize(e, signature) {
  e.stopPropagation()
  currentDragSignature = signature
  isResizing.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  
  resizeStartWidthPct = signature.width
  resizeStartHeightPct = signature.height
  
  // Get container dimensions to calculate % delta
  // The handle is inside the signature div, which is inside the container div
  const sigDiv = e.target.parentElement 
  const container = sigDiv.parentElement
  if (container) {
    const rect = container.getBoundingClientRect()
    currentDragContainerWidth = rect.width
    currentDragContainerHeight = rect.height
  }
  
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}

function onResize(e) {
  if (!currentDragSignature || !isResizing.value) return
  
  const deltaX = e.clientX - dragStartX
  
  // Calculate delta %
  const deltaXPct = (deltaX / currentDragContainerWidth) * 100
  
  // Update width %
  const newWidthPct = Math.max(5, resizeStartWidthPct + deltaXPct) // Min 5% width
  
  // Maintain aspect ratio
  // ratio = height% / width% ?? No, ratio is visual.
  // Visual Ratio = (HeightPx / WidthPx). 
  // Height% = (HeightPx / ContainerHeight) * 100
  // Width% = (WidthPx / ContainerWidth) * 100
  // So Height% = (WidthPx * Ratio / ContainerHeight) * 100
  //             = ((Width% * ContainerWidth / 100) * Ratio / ContainerHeight) * 100
  //             = Width% * Ratio * (ContainerWidth / ContainerHeight)
  
  // Simplified: preserve the INITIAL ratio of percentages
  // IF the container aspect ratio doesn't change (which it behaves like a scaled image), 
  // then maintaining the ratio of % is correct.
  const ratioPct = resizeStartHeightPct / resizeStartWidthPct
  
  currentDragSignature.width = newWidthPct
  currentDragSignature.height = newWidthPct * ratioPct
}

function stopResize() {
  isResizing.value = false
  currentDragSignature = null
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
})
</script>

<style scoped>
/* Scrollbar for Webkit browsers (Chrome, Edge, Safari) */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;               /* Scrollbar width */
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;      /* Track color */
  border-radius: 6px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #2ecc71; /* Thumb color */
  border-radius: 6px;
  border: 2px solid #f1f1f1; /* Optional: padding effect */
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #24bd64;
}

/* Firefox */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #2ecc71 #f1f1f1;
}
/* Add smooth transitions */
.group {
  @apply cursor-not-allowed;
}

.group:hover {
  @apply cursor-not-allowed;
}
</style>
